import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileText, Send, Sparkles, ChevronDown, ChevronRight, Droplets, Zap, ArrowRight, Heart, ExternalLink, Check, Pencil, Layers, FilePlus2, AlertTriangle } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Kfw458Block from "@/components/Kfw458Block";
import WirtschaftlichkeitBlock from "@/components/WirtschaftlichkeitBlock";
import SafeBoundary from "@/components/SafeBoundary";
import RecommendedHeatPumpsBlock from "./RecommendedHeatPumpsBlock";
import WpPackageConfiguratorV3 from "./WpPackageConfiguratorV3";
import { DictaphoneTextarea } from "@/components/DictaphoneTextarea";

const NOVATUS_CARE_URL = process.env.REACT_APP_NOVATUS_CARE_URL || "https://novatus-care.de";

const fmtEur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

const CATEGORY_LABELS = {
  guenstig: { label: "Günstig", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  komfort:  { label: "Komfort", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  premium:  { label: "Premium", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
};

const CategoryPill = ({ category }) => {
  const cfg = CATEGORY_LABELS[category] || CATEGORY_LABELS.guenstig;
  return (
    <span
      data-testid={`category-pill-${category}`}
      className={`px-2 py-0.5 text-[12px] font-bold uppercase tracking-wider rounded border ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
};

const COMP_CATEGORY_LABELS = {
  waermepumpe: "Wärmepumpe",
  brauchwasserspeicher: "Brauchwasserspeicher",
  heizungspuffer: "Heizungspuffer",
  fundament: "Fundament",
  montage: "Montage & Hydraulik",
  elektroinstallation: "Elektroinstallation",
  demontage: "Demontage",
  inbetriebnahme: "Inbetriebnahme",
  solarthermie: "Solarthermie-Anbindung",
  pv_kopplung: "PV-Kopplung",
  wartung: "Wartung",
  sonstiges: "Sonstiges",
};

// New configurator structure: Zusatzkomponenten are bundled into two
// collapsible groups (Hydraulisch vs. Elektrisch). Every item inside is a
// checkbox — default unchecked, adds Aufpreis when selected.
// Konfigurator-Gruppen: Hydraulisch + Elektrisch.
// Beide Gruppen mappen auf die kanonischen Component-Kategorien `hydraulik`
// und `elektro` (siehe `scripts/reset_wp_catalog.py`).
const HYDRAULIC_CATEGORIES = ["hydraulik"];
const ELECTRIC_CATEGORIES = ["elektro"];

// Legacy radio/service splits — only kept so old packages with included_service_component_ids
// continue to behave correctly. New packages leave both arrays/objects empty.
const RADIO_CATEGORIES = ["brauchwasserspeicher", "heizungspuffer", "fundament"];
const SERVICE_CATEGORIES = ["montage", "elektroinstallation", "demontage", "inbetriebnahme"];
const OPTIONAL_CATEGORIES = ["solarthermie", "pv_kopplung", "wartung", "sonstiges"];

// --- Collapsible Zusatzkomponenten-Gruppe ----------------------------------
// Two of these are rendered inside the ConfiguratorPanel: Hydraulisch +
// Elektrisch. Each shows a header with summary (count + €), expands on click
// and reveals a flat checkbox list of items spanning multiple categories.
function AddonGroup({
  groupKey, title, icon: Icon, categories, componentsByCategory, optionalPicks,
  setOptionalPicks, defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const fmt = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);
  const items = categories.flatMap((cat) => (componentsByCategory[cat] || []).map((c) => ({ ...c, _cat: cat })));
  if (items.length === 0) return null;
  const selectedCount = items.filter((c) => !!optionalPicks[c.id]).length;
  const selectedSum = items.reduce((s, c) => s + (optionalPicks[c.id] ? Number(c.price_net) || 0 : 0), 0);

  return (
    <div
      data-testid={`offer-config-group-${groupKey}`}
      className={`rounded-xl border ${selectedCount > 0 ? "border-[#00E575]/40 bg-[#0d2a1a]/40" : "border-zinc-800 bg-[#09090B]"} overflow-hidden`}
    >
      <button
        type="button"
        data-testid={`offer-config-group-toggle-${groupKey}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-zinc-900/60 transition"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${selectedCount > 0 ? "bg-[#00E575]/15 text-[#00E575]" : "bg-zinc-800 text-zinc-300"}`}>
            <Icon size={18} />
          </div>
          <div className="text-left flex-1 min-w-0">
            <div className="text-[15px] font-bold text-zinc-100 truncate">{title}</div>
            <div className="text-[12px] text-zinc-200 font-medium">
              {selectedCount > 0
                ? `${selectedCount} ausgewählt · ${fmt(selectedSum)}`
                : `${items.length} Optionen verfügbar — antippen, um auszuwählen`}
            </div>
          </div>
        </div>
        {open ? <ChevronDown size={18} className="text-zinc-400 shrink-0" /> : <ChevronRight size={18} className="text-zinc-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-1.5">
          {items.map((c) => {
            const checked = !!optionalPicks[c.id];
            return (
              <label
                key={c.id}
                data-testid={`offer-config-addon-${c.id}`}
                className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition ${checked ? "bg-[#00E575]/10 border border-[#00E575]" : "bg-[#0a0a0c] border border-zinc-800 hover:border-zinc-600"}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => setOptionalPicks({ ...optionalPicks, [c.id]: !checked })}
                  className="mt-1 w-4 h-4 accent-[#00E575] shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-zinc-100">{c.title}</div>
                  {c.description && <div className="text-[12px] text-zinc-200 font-medium mt-0.5 leading-snug">{c.description}</div>}
                </div>
                <div className={`text-xs font-mono shrink-0 ${checked ? "text-[#00E575] font-semibold" : "text-zinc-400"}`}>
                  {checked ? "+ " : ""}{fmt(c.price_net)}
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}


function ConfiguratorPanel({
  pkg, componentsByCategory, radioPicks, servicePicks, optionalPicks,
  setRadioPicks, setServicePicks, setOptionalPicks, configuredTotal, open, setOpen,
  onCreateOffer, creating, editMode,
}) {
  const includedSvc = pkg.included_service_component_ids || [];
  const fmt = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);
  const bundle = Number(pkg.bundle_price_net) || 0;
  const delta = configuredTotal - bundle;

  // ---- Zusammenfassung der aktuell aktiven Komponenten ---- //
  // Alles, was momentan zum Endpreis beiträgt. Hilft dem Rep auf einen Blick
  // zu sehen, was im Bundle steckt.
  const allComps = Object.values(componentsByCategory).flat().reduce(
    (m, c) => ((m[c.id] = c), m), {});
  const activeIncluded = [];
  Object.values(radioPicks).forEach((id) => {
    if (allComps[id]) activeIncluded.push({ ...allComps[id], _slot: "radio" });
  });
  Object.entries(servicePicks).forEach(([id, picked]) => {
    if (picked && allComps[id]) activeIncluded.push({ ...allComps[id], _slot: "service" });
  });
  Object.entries(optionalPicks).forEach(([id, picked]) => {
    if (picked && allComps[id] && !includedSvc.includes(id)) {
      activeIncluded.push({ ...allComps[id], _slot: "extra" });
    }
  });

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid="offer-configurator">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold">Festpreis-Paket</h3>
          <p className="text-[12.5px] text-zinc-200 font-medium leading-snug max-w-md">
            Standard-Komponenten sind bereits enthalten. Optionen können getauscht, weggelassen oder hinzugefügt werden.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-bold">Endpreis (brutto)</div>
          <div className="font-mono text-3xl text-emerald-300 font-semibold" data-testid="offer-configurator-total">
            {fmt(configuredTotal * 1.19)}
          </div>
          <div className="text-[10.5px] font-mono text-zinc-500 mt-0.5">
            Netto {fmt(configuredTotal)}
          </div>
          {Math.abs(delta) > 0.01 && (
            <div className={`text-[11.5px] font-mono font-semibold ${delta > 0 ? "text-amber-300" : "text-zinc-300"}`}>
              {delta > 0 ? "+" : ""}{fmt(delta * 1.19)} ggü. Basispreis {fmt(bundle * 1.19)}
            </div>
          )}
        </div>
      </div>

      {/* Im Paket enthalten – kompakter Überblick */}
      <div className="bg-[#0d2a1a] border border-[#00E575]/30 rounded-xl p-3 mb-3" data-testid="offer-configurator-included">
        <div className="text-[10.5px] uppercase tracking-widest text-[#00E575] font-extrabold mb-2">
          Im Paket enthalten · {activeIncluded.length} Positionen
        </div>
        <ul className="space-y-1 text-[13.5px] font-medium text-zinc-100">
          {activeIncluded.map((c) => (
            <li key={c.id} className="flex items-start gap-2">
              <span className="text-[#00E575] mt-0.5">✓</span>
              <span className="flex-1 min-w-0 truncate">{c.title}</span>
              {c._slot === "extra" && (
                <span className="text-[12px] text-amber-300 uppercase tracking-wider font-extrabold">+ Zusatz</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* PRIMARY CTA — directly under the Endpreis so it is reachable on
          small phones (iPhone SE) without scrolling past the entire
          Zusatzkomponenten block. */}
      {onCreateOffer && (
        <button
          data-testid="offer-create-btn-primary"
          onClick={onCreateOffer}
          disabled={creating}
          className="mb-3 h-14 w-full bg-[#00E575] text-[#09090B] font-bold text-[16px] rounded-xl hover:bg-[#00C964] glow-green active:scale-[0.97] transition flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {creating ? <Loader2 className="animate-spin" /> : editMode ? (<><FileText size={20} /> Angebot aktualisieren (Nummer bleibt)</>) : (<><FileText size={20} /> Angebot erstellen & PDF generieren</>)}
        </button>
      )}

      <button
        onClick={() => setOpen(!open)}
        data-testid="offer-configurator-toggle"
        className={`mb-3 h-14 w-full font-bold text-[16px] rounded-xl active:scale-[0.97] transition flex items-center justify-center gap-2 ${
          open
            ? "bg-[#F97316] text-[#09090B] hover:bg-[#EA6A0A] shadow-[0_0_18px_rgba(249,115,22,0.32)]"
            : "bg-[#F97316] text-[#09090B] hover:bg-[#EA6A0A] shadow-[0_0_18px_rgba(249,115,22,0.32)]"
        }`}
      >
        {open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        <span>Komponenten {open ? "ausblenden" : "anpassen / hinzufügen"}</span>
      </button>

      {open && (
        <div className="space-y-4">
          {/* --- LEGACY: Radio-Kategorien (nur bei alten Paketen mit default_radio_components) --- */}
          {RADIO_CATEGORIES.map((cat) => {
            const opts = componentsByCategory[cat] || [];
            const defaultId = (pkg.default_radio_components || {})[cat];
            if (opts.length === 0 || !defaultId) return null;
            const pickedId = radioPicks[cat] || defaultId;
            return (
              <div key={cat} data-testid={`offer-config-radio-${cat}`}>
                <div className="text-xs uppercase tracking-widest text-zinc-300 mb-2">
                  {COMP_CATEGORY_LABELS[cat]}
                </div>
                <div className="space-y-1.5">
                  {opts.map((c) => {
                    const isDefault = c.id === defaultId;
                    const checked = pickedId === c.id;
                    const diff = (c.price_net || 0) - ((opts.find((o) => o.id === defaultId)?.price_net) || 0);
                    return (
                      <label key={c.id} className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition ${checked ? "bg-[#00E575]/10 border border-[#00E575]" : "bg-[#09090B] border border-zinc-800 hover:border-zinc-600"}`}>
                        <input
                          type="radio"
                          name={`radio-${cat}`}
                          checked={checked}
                          onChange={() => setRadioPicks({ ...radioPicks, [cat]: c.id })}
                          className="mt-1 w-4 h-4 accent-[#00E575]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                            {c.title}
                            {isDefault && <span className="px-1.5 py-0.5 text-[12px] uppercase tracking-wider rounded bg-zinc-800 text-zinc-400">Standard</span>}
                          </div>
                          {c.description && <div className="text-xs text-zinc-300 mt-0.5">{c.description}</div>}
                        </div>
                        <div className="text-xs font-mono text-zinc-300 text-right flex-shrink-0">
                          {isDefault ? "im Paket" : (diff >= 0 ? "+ " : "− ") + fmt(Math.abs(diff))}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* --- LEGACY: Service-Defaults (nur wenn included_service_component_ids gesetzt) --- */}
          {SERVICE_CATEGORIES.map((cat) => {
            const opts = (componentsByCategory[cat] || []).filter((c) => includedSvc.includes(c.id));
            if (opts.length === 0) return null;
            return (
              <div key={cat} data-testid={`offer-config-service-${cat}`}>
                <div className="text-xs uppercase tracking-widest text-zinc-300 mb-2">
                  {COMP_CATEGORY_LABELS[cat]} <span className="text-[12px] normal-case text-zinc-400">(im Paket – abwählbar)</span>
                </div>
                <div className="space-y-1.5">
                  {opts.map((c) => {
                    const checked = !!servicePicks[c.id];
                    return (
                      <label key={c.id} className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition ${checked ? "bg-[#09090B] border border-zinc-700" : "bg-[#09090B] border border-dashed border-zinc-800 opacity-60"}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setServicePicks({ ...servicePicks, [c.id]: !checked })}
                          className="mt-1 w-4 h-4 accent-[#00E575]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{c.title}</div>
                          {c.description && <div className="text-xs text-zinc-300 mt-0.5">{c.description}</div>}
                        </div>
                        <div className="text-xs font-mono text-zinc-300 flex-shrink-0">
                          {checked ? "im Paket" : "− " + fmt(c.price_net)}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* --- NEU: Zusatzkomponenten in 2 zuklappbaren Gruppen --- */}
          <div className="pt-1">
            <div className="text-[11.5px] uppercase tracking-widest text-zinc-200 mb-2 font-extrabold">
              Zusatzkomponenten
            </div>
            <div className="space-y-2">
              <AddonGroup
                groupKey="hydraulic"
                title="Hydraulisch"
                icon={Droplets}
                categories={HYDRAULIC_CATEGORIES}
                componentsByCategory={componentsByCategory}
                optionalPicks={optionalPicks}
                setOptionalPicks={setOptionalPicks}
              />
              <AddonGroup
                groupKey="electric"
                title="Elektrisch"
                icon={Zap}
                categories={ELECTRIC_CATEGORIES}
                componentsByCategory={componentsByCategory}
                optionalPicks={optionalPicks}
                setOptionalPicks={setOptionalPicks}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OfferSection({ project, customer, consumption, offers, onUpdate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isManagerOrAdmin = user?.role === "admin" || user?.role === "sales_manager";
  // Edit-Mode aktiv, wenn ?edit=<offer_id> in der URL steht → PATCH statt POST,
  // Angebotsnummer + created_at bleiben erhalten (KfW-458 Referenz-Stabilität).
  // useSearchParams sorgt dafür, dass dieser Wert reaktiv aktualisiert wird,
  // wenn ein Child-Button (z.B. "Konfigurator" in OffersListSection) via
  // navigate() den Query-Param setzt.
  const editingOfferId = searchParams.get("edit") || null;
  const editingOffer = editingOfferId ? (offers || []).find((o) => o.id === editingOfferId) : null;
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [selectedItems, setSelectedItems] = useState({}); // item_id -> bool

  // Discount fields + caps
  const [discountPct, setDiscountPct] = useState("");
  const [discountEur, setDiscountEur] = useState("");
  const [caps, setCaps] = useState({ max_discount_percent: null, max_discount_eur: null });
  // Freier Bemerkungs-Text des Verkäufers — erscheint im Angebots-PDF.
  const [remarks, setRemarks] = useState("");
  // Preis-Transparenz-Toggle (Iteration 73): wenn true, zeigt das PDF
  // je Komponente einen Einzelpreis (statt nur Bündelpreis unten).
  const [showComponentPrices, setShowComponentPrices] = useState(false);

  // Aktive Vertriebskategorie für die WP-Paket-Auswahl
  const [activeCategory, setActiveCategory] = useState("guenstig");

  // Komponenten-Konfigurator
  const [components, setComponents] = useState([]);          // alle aktiven Komponenten
  const [configOpen, setConfigOpen] = useState(true);       // Konfigurator-Panel auf/zu
  const [radioPicks, setRadioPicks] = useState({});          // {category: component_id}
  const [servicePicks, setServicePicks] = useState({});      // {component_id: bool}
  const [optionalPicks, setOptionalPicks] = useState({});    // {component_id: bool} (Solarthermie/PV/Wartung)

  // -------- Konfigurator (vereinheitlicht auf V3 seit 09.07.2026) --------
  const [v3Config, setV3Config] = useState(null); // {packageId, lines, totals, discountPct}
  const handleV3Change = useCallback((c) => setV3Config(c), []);

  // Section refs powering the sticky step-nav (① Paket · ② Förderung · ③ Amortisation)
  const paketRef = useRef(null);
  const foerderungRef = useRef(null);
  const wirtschaftlichkeitRef = useRef(null);
  const [activeStep, setActiveStep] = useState("paket");

  // Highlight the section currently in view as the user scrolls.
  useEffect(() => {
    const sections = [
      { id: "paket", el: paketRef.current },
      { id: "foerderung", el: foerderungRef.current },
      { id: "amortisation", el: wirtschaftlichkeitRef.current },
    ].filter((s) => s.el);
    if (!sections.length) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          const id = sections.find((s) => s.el === visible[0].target)?.id;
          if (id) setActiveStep(id);
        }
      },
      // Section is "active" once its top crosses the line just below the
      // sticky step-nav (top: 168px ≈ header + tabs + step-nav height).
      { rootMargin: "-168px 0px -55% 0px", threshold: [0, 0.1, 0.3, 0.6] },
    );
    sections.forEach((s) => io.observe(s.el));
    return () => io.disconnect();
  }, []);

  const scrollToStep = (id) => {
    const map = {
      paket: paketRef,
      foerderung: foerderungRef,
      amortisation: wirtschaftlichkeitRef,
    };
    const el = map[id]?.current;
    if (!el) return;
    setActiveStep(id);
    const y = el.getBoundingClientRect().top + window.pageYOffset - 168;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };

  const heatLoad = consumption?.calculated_heat_load_kw || null;

  useEffect(() => {
    const params = heatLoad != null ? { heat_load_kw: heatLoad } : {};
    Promise.all([
      api.get("/price-packages", { params }),
      api.get("/components").catch(() => ({ data: [] })),
    ])
      .then(([rPkg, rComp]) => {
        setPackages(rPkg.data);
        setComponents(rComp.data || []);
        // Prefer editing offer if in edit mode, else recommended, else first
        let target = null;
        if (editingOffer) {
          target = rPkg.data.find((p) => p.id === editingOffer.package_id) || rPkg.data[0];
        } else {
          target = rPkg.data.find((p) => p.recommended) || rPkg.data[0];
        }
        if (target) {
          setSelectedPkg(target.id);
          setSelectedItems(Object.fromEntries((target.items || []).map((i) => [i.id, !i.optional])));
          if (target.category) setActiveCategory(target.category);
          applyPackageDefaults(target);
        }
        // In edit-mode also prefill discount + remarks from the existing offer
        if (editingOffer) {
          setDiscountPct(editingOffer.discount_percent_requested ? String(editingOffer.discount_percent_requested) : "");
          setDiscountEur(editingOffer.discount_eur_requested ? String(editingOffer.discount_eur_requested) : "");
          setRemarks(editingOffer.remarks || "");
          setShowComponentPrices(!!editingOffer.show_component_prices);
        }
      })
      .finally(() => setLoading(false));
    // Rabatt-Caps + (neues Angebot) Preistoggle-Default des Mandanten – für alle Rollen lesbar
    api.get("/sales-context")
      .then((r) => {
        setCaps(r.data || {});
        if (!editingOffer) setShowComponentPrices(!!r.data?.default_show_component_prices);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatLoad]);

  // Set radio / service / optional picks from a package's defaults
  const applyPackageDefaults = (p) => {
    const radio = p?.default_radio_components || {};
    setRadioPicks({ ...radio });
    const services = {};
    (p?.included_service_component_ids || []).forEach((id) => { services[id] = true; });
    setServicePicks(services);
    setOptionalPicks({});
  };

  const pkg = packages.find((p) => p.id === selectedPkg);

  const toggleItem = (id) => setSelectedItems({ ...selectedItems, [id]: !selectedItems[id] });

  // ---- Konfigurator: nach Kategorie gruppierte Komponenten ---- //
  const componentsByCategory = useMemo(() => {
    const g = {};
    components.forEach((c) => {
      if (!c.active) return;
      (g[c.category] = g[c.category] || []).push(c);
    });
    Object.keys(g).forEach((k) =>
      g[k].sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100))
    );
    return g;
  }, [components]);

  const compById = useMemo(() => {
    const m = {};
    components.forEach((c) => { m[c.id] = c; });
    return m;
  }, [components]);

  // Live-Preis aus Konfigurator: Δ vom bundle_price_net des Pakets
  const configuredTotal = useMemo(() => {
    if (!pkg) return 0;
    const bundle = Number(pkg.bundle_price_net) || 0;
    if (!bundle) {
      // Fallback: alte items-basierte Logik (selectedItems direkt)
      return (pkg.items || []).reduce((sum, it) => {
        if (selectedItems[it.id]) return sum + (it.quantity || 1) * (it.unit_price_net || 0);
        return sum;
      }, 0);
    }
    // Δ Radio: Differenz aktuelle Auswahl vs. Default
    const defaults = pkg.default_radio_components || {};
    let delta = 0;
    Object.keys(radioPicks).forEach((cat) => {
      if (cat === "waermepumpe") return; // WP ist Paket-fix
      const picked = compById[radioPicks[cat]];
      const def = compById[defaults[cat]];
      if (picked && def) delta += (picked.price_net || 0) - (def.price_net || 0);
    });
    // Δ Service-Defaults: nur diejenigen aus included_service_component_ids,
    // die aktuell abgewählt sind, ziehen den Preis ab.
    const included = pkg.included_service_component_ids || [];
    included.forEach((id) => {
      if (!servicePicks[id]) {
        const c = compById[id];
        if (c) delta -= (c.price_net || 0);
      }
    });
    // Δ Optionale Add-ons (Solarthermie, PV, Wartung, ggf. zusätzliche Services)
    Object.keys(optionalPicks).forEach((id) => {
      if (!optionalPicks[id]) return;
      // schon im Bundle? dann nicht doppelt
      if (included.includes(id)) return;
      const c = compById[id];
      if (c) delta += (c.price_net || 0);
    });
    return Math.max(0, bundle + delta);
  }, [pkg, radioPicks, servicePicks, optionalPicks, compById, selectedItems]);

  // Die für Backend (POST /offers) zusammengestellten Items.
  // Wenn der Konfigurator aktiv ist und das Paket einen bundle_price_net hat,
  // bauen wir die Items aus den ausgewählten Komponenten — sonst Legacy-items.
  const configuredItems = useMemo(() => {
    if (!pkg || !pkg.bundle_price_net) return null;
    const items = [];
    // 1) Paket-Wärmepumpe als erstes Item.
    // Legacy-Pakete haben sie als "waermepumpe"-Radio-Komponente
    // (`default_radio_components.waermepumpe`). Neue Bundle-Pakete
    // (HEIKO Thermal Plus / LG Therma V R290) haben das Komplettpaket
    // stattdessen direkt in `pkg.items[0]` mit `unit_price_net = bundle_net`.
    const wpId = (pkg.default_radio_components || {}).waermepumpe;
    const wp = compById[wpId];
    if (wp) {
      items.push({ ...wp, _isWp: true });
    } else if (pkg.items && pkg.items.length > 0) {
      const bundleItem = pkg.items[0];
      items.push({
        id: bundleItem.id,
        title: bundleItem.title,
        description: bundleItem.description || "",
        price_net: Number(bundleItem.unit_price_net) || 0,
        vat_rate: Number(bundleItem.vat_rate) || 0.19,
        _isWp: true,
      });
    }
    // Andere Radios
    Object.entries(radioPicks).forEach(([cat, id]) => {
      if (cat === "waermepumpe") return;
      const c = compById[id];
      if (c) items.push(c);
    });
    // Services (Defaults + ggf. abgewählte excluded)
    Object.entries(servicePicks).forEach(([id, picked]) => {
      if (!picked) return;
      const c = compById[id];
      if (c) items.push(c);
    });
    // Optionale Add-ons
    Object.entries(optionalPicks).forEach(([id, picked]) => {
      if (!picked) return;
      if ((pkg.included_service_component_ids || []).includes(id)) return;
      const c = compById[id];
      if (c) items.push(c);
    });
    return items;
  }, [pkg, radioPicks, servicePicks, optionalPicks, compById]);

  const subtotal = useMemo(() => {
    // Iter88j (2026-02-19): v3-Konfigurator (Zeilentabelle) ist seit 09.07.2026
    // der einzige aktive Modus zum Erstellen von Angeboten. Wenn der User dort
    // Zeilen konfiguriert hat, MUSS der KfW-Block auch mit der v3-Summe rechnen
    // — sonst greift stale `configuredTotal` aus dem Legacy-Konfigurator
    // (führte zum Koch-Bug: 25.912,26 € statt 34.468 € brutto → falscher Cap).
    if (v3Config?.lines?.length) {
      return v3Config.lines.reduce(
        (sum, L) => sum + (Number(L.quantity) || 1) * (Number(L.unit_price_net) || 0),
        0,
      );
    }
    // Legacy: Konfigurator-v2 aktiv (bundle_price_net + configuredItems)
    if (pkg?.bundle_price_net && configuredItems) return configuredTotal;
    // Fallback: älteste items-Logik
    return pkg?.items?.reduce((sum, it) => {
      if (selectedItems[it.id]) return sum + (it.quantity || 1) * (it.unit_price_net || 0);
      return sum;
    }, 0) || 0;
  }, [v3Config, pkg, configuredItems, configuredTotal, selectedItems]);

  // Effective live discount, mirrors backend logic
  const discountInfo = useMemo(() => {
    const reqPct = parseFloat(discountPct) || 0;
    const reqEur = parseFloat(discountEur) || 0;
    const requested = Math.max(
      Math.round(subtotal * reqPct) / 100,
      Math.round(reqEur * 100) / 100,
    );
    const caps_pct = caps.max_discount_percent;
    const caps_eur = caps.max_discount_eur;
    const candidates = [];
    if (caps_pct != null) candidates.push(Math.round(subtotal * caps_pct) / 100);
    if (caps_eur != null) candidates.push(caps_eur);
    const cap = candidates.length ? Math.min(...candidates) : 0;
    const applied = Math.max(0, Math.min(requested, cap));
    return { requested, cap, applied, capped: requested > cap + 0.005 };
  }, [discountPct, discountEur, subtotal, caps]);

  const netAfter = Math.max(0, subtotal - discountInfo.applied);
  const totalVat = netAfter * 0.19;
  const totalGross = netAfter + totalVat;

  const editingLocked = !!(editingOffer?.sent_at || ["sent", "versendet", "signed"].includes(editingOffer?.status));

  const createOffer = async ({ forceNew = false } = {}) => {
    // v3-Konfigurator (Zeilen-Tabelle, Easybill-Format) — einziger Modus seit 09.07.2026
    {
      const cfg = v3Config;
      if (!cfg?.packageId || !cfg?.lines?.length) {
        toast.error("Bitte zuerst ein Paket wählen und mindestens eine Zeile behalten.");
        return;
      }
      setCreating(true);
      try {
        // Alle Zeilen als is_premarged=true — Preise sind vom Rep bereits fertig konfiguriert.
        const configured_items = cfg.lines.map((L) => ({
          title: (L.title || "").trim() || "Position",
          description: [L.sku ? `Art.-Nr.: ${L.sku}` : "", L.description || ""].filter(Boolean).join(" · "),
          quantity: Number(L.quantity) || 1,
          unit_price_net: Number(L.unit_price_net) || 0,
          vat_rate: Number(L.vat_rate) || 0.19,
          optional: false,
          is_premarged: true,
          category: L.category && L.category !== "frei" ? L.category : null,
        }));
        const body = {
          package_id: cfg.packageId,
          discount_percent: Number(cfg.discountPct) || 0,
          discount_eur: Number(cfg.discountEur) || 0,
          remarks: (remarks || "").trim() || null,
          show_component_prices: !!showComponentPrices,
          configured_items,
          wp_customization: {
            source: "wp_packages",
            configurator: "v3",
            package_id: cfg.packageId,
            lines: cfg.lines,  // vollständiger Snapshot inkl. Kategorie/SKU für Easybill-Push
          },
        };
        // Versendete Angebote dürfen nicht überschrieben werden → automatisch neue Version (alte wird „abgelöst“).
        if (editingOfferId && !forceNew && !editingLocked) {
          const r = await api.patch(`/offers/${editingOfferId}`, body);
          try {
            await api.post(`/offers/${editingOfferId}/generate-pdf`);
          } catch {
            toast.error("Angebot gespeichert, aber das PDF konnte nicht neu erstellt werden – bitte im Tab „Angebote“ erneut erzeugen.");
          }
          if (r.data.discount_was_capped) toast.warning("Rabatt auf Maximum begrenzt.");
          toast.success(`Angebot ${r.data.offer?.offer_number || ""} aktualisiert · PDF neu erstellt`);
          setRemarks("");
          onUpdate?.();
          navigate(`/projects/${project.id}?tab=offers`, { replace: true });
          return;
        }
        const r = await api.post(`/projects/${project.id}/offers`, body);
        await api.post(`/offers/${r.data.id}/generate-pdf`);
        if (r.data.discount_was_capped) toast.warning("Rabatt auf Maximum begrenzt.");
        toast.success(`Angebot ${r.data.offer_number} erstellt`);
        setRemarks("");
        onUpdate?.();
        navigate(`/projects/${project.id}?tab=offers`, { replace: false });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Fehler beim Erstellen");
      } finally { setCreating(false); }
      return;
    }

  };

  // Iter88zz — Orientierungsangebot: unverbindliches Erst-PDF ohne LLV/AGB/
  // Signatur. Nutzt das aktuell konfigurierte Paket (falls vorhanden) samt
  // Preis 1:1. Fällt zurück auf das letzte Angebot des Projekts.
  const createOrientationOffer = async () => {
    setCreating(true);
    try {
      const body = {};
      if (v3Config?.packageId) body.package_id = v3Config.packageId;
      if (heatLoad) body.heat_load_kw = heatLoad;
      if (totalGross > 0) body.package_price_gross = totalGross;
      if ((remarks || "").trim()) body.remarks = remarks.trim();
      const r = await api.post(`/projects/${project.id}/orientation-offers`, body);
      toast.success(`Orientierungsangebot ${r.data.offer_number} erzeugt — PDF wird geöffnet…`);
      // PDF im neuen Tab öffnen (Auth via ?auth-Token in fileUrl-Helper)
      if (r.data.pdf_storage_path) {
        window.open(fileUrl(r.data.pdf_storage_path), "_blank");
      }
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Orientierungsangebot konnte nicht erstellt werden");
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-zinc-400"><Loader2 className="animate-spin inline mr-2" /> Lade Pakete…</div>;

  return (
    <div className="space-y-5 pb-24">
      {editingOfferId && editingOffer && (
        <div
          className={`rounded-2xl border p-4 ${editingOffer.sent_at ? "border-red-500/50 bg-red-500/8" : "border-amber-500/40 bg-amber-500/5"}`}
          data-testid="offer-edit-banner"
        >
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${editingOffer.sent_at ? "bg-red-500/25" : "bg-amber-500/20"}`}>
              {editingOffer.sent_at ? <AlertTriangle size={16} className="text-red-300" /> : <Pencil size={16} className="text-amber-300" />}
            </div>
            <div className="flex-1">
              <div className={`font-extrabold text-[14px] ${editingOffer.sent_at ? "text-red-200" : "text-amber-200"}`}>
                {editingOffer.sent_at
                  ? `Dieses Angebot wurde bereits versendet — Angebot ${editingOffer.offer_number}`
                  : `Bearbeitungsmodus — Angebot ${editingOffer.offer_number}`}
              </div>
              <p className={`text-[12.5px] mt-0.5 leading-snug ${editingOffer.sent_at ? "text-red-100/85" : "text-amber-100/80"}`}>
                {editingOffer.sent_at ? (
                  <>
                    Beim Speichern entsteht ein <b className="text-red-100">neues Angebot mit neuer Nummer</b>. Das aktuelle Angebot wird als <b>abgelöst</b> markiert, der QR-Code gesperrt und dem Kunden im PDF ein „VERALTET"-Wasserzeichen eingeblendet.
                    {editingOffer.sent_at && (
                      <> · Versendet am <b className="text-red-100">{new Date(editingOffer.sent_at).toLocaleDateString("de-DE", { day:"2-digit", month:"2-digit", year:"numeric" })}</b></>
                    )}
                  </>
                ) : (
                  <>Beim Speichern werden Paket / Positionen ersetzt. <b className="text-amber-100">Angebotsnummer und Erstell-Datum bleiben</b> unverändert (wichtig für KfW-458-BzA/BnD).</>
                )}
              </p>
              <button
                type="button"
                onClick={() => navigate(`/projects/${project.id}?tab=offers`)}
                className={`mt-2 h-8 px-3 text-[11.5px] font-semibold rounded-lg border ${editingOffer.sent_at ? "bg-red-500/20 hover:bg-red-500/30 text-red-100 border-red-500/40" : "bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border-amber-500/40"}`}
                data-testid="offer-edit-cancel"
              >
                Bearbeitung abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sticky Step-Navigation — 3 nummerierte Pills für den Konfigurator. */}
      <div className="sticky top-[60px] xl:top-[112px] z-20 -mx-4 sm:mx-0 bg-[#09090B]/95 backdrop-blur-md border-b border-zinc-800 px-4 sm:px-0 sm:border-0 sm:rounded-xl sm:bg-[#0E0E12]">
        <div className="flex overflow-x-auto gap-1 py-2 -mx-1 px-1 hide-scrollbar">
          {[
            { id: "paket",        n: "①", label: "Paket" },
            { id: "foerderung",   n: "②", label: "Förderung" },
            { id: "amortisation", n: "③", label: "Amortisation" },
          ].map((s) => {
            const active = activeStep === s.id;
            return (
              <button
                key={s.id}
                type="button"
                data-testid={`offer-step-${s.id}`}
                onClick={() => scrollToStep(s.id)}
                className={`flex items-center gap-1.5 h-9 px-3 rounded-full text-[12.5px] font-bold whitespace-nowrap transition-all active:scale-95 ${
                  active
                    ? "bg-[#00E575] text-[#09090B] shadow-md shadow-[#00E575]/30"
                    : "bg-[#18181B] text-zinc-300 border border-zinc-800 hover:border-zinc-600"
                }`}
              >
                <span className={`font-mono text-[14px] leading-none ${active ? "text-[#09090B]" : "text-[#00E575]"}`}>{s.n}</span>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>


      {/* Step ① Heat load reminder + Package selection */}
      <div ref={paketRef} className="space-y-5 scroll-mt-[168px]">
      {/* Heat load reminder */}
      {!heatLoad && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 text-sm text-amber-300">
          ⚠️ Heizlast noch nicht berechnet. Bitte zuerst im Tab „Verbrauch" die Heizlast ermitteln.
        </div>
      )}

      {/* Plausibilitätswarnung wenn error im Verbrauch */}
      {consumption?.plausibility?.severity === "error" && (
        <div className="bg-red-950/40 border-2 border-red-500/50 rounded-2xl p-4 text-sm text-red-200" data-testid="offer-plausibility-warning">
          <div className="font-bold text-red-300 mb-1">⚠️ Verbrauchsdaten möglicherweise fehlerhaft</div>
          <div className="text-zinc-300">
            Die Plausibilitätsprüfung im Tab „Verbrauch" zeigt einen kritischen Hinweis. Bitte vor dem Versand der Kostenschätzung korrigieren – sonst könnte das Angebot deutlich von der realen Nachfrage abweichen.
          </div>
        </div>
      )}

      {/* Konfigurator (vereinheitlicht auf Zeilen-Tabelle · Rechtsstand 09.07.2026) */}
      <WpPackageConfiguratorV3
        heatLoadKw={heatLoad}
        customerId={customer?.id || project?.customer_id || null}
        initialPackageId={editingOffer?.wp_customization?.package_id || editingOffer?.package_id || null}
        initialLines={editingOffer?.wp_customization?.lines || null}
        editingSnapshot={editingOffer}
        initialDiscountPct={editingOffer?.discount_percent_requested ?? editingOffer?.discount_percent ?? 0}
        initialDiscountEur={editingOffer?.discount_eur_requested ?? 0}
        onChange={handleV3Change}
      />
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 space-y-3" data-testid="offer-cfg-v3-tail">
        <div>
          <label className="block text-[12px] uppercase tracking-wider text-zinc-300 mb-1">Bemerkungen zum Auftrag (erscheinen im PDF)</label>
          <DictaphoneTextarea value={remarks} onChange={setRemarks} testid="offer-v3-remarks" rows={3}
            placeholder="Besonderheiten, Einbindung, elektrische Absicherung … (oder Mikrofon-Button)" />
        </div>
        <label className="flex items-start gap-2.5 cursor-pointer bg-[#0F0F11] border border-zinc-800 rounded-lg px-3 py-2.5">
          <input
            type="checkbox"
            checked={showComponentPrices}
            onChange={(e) => setShowComponentPrices(e.target.checked)}
            data-testid="offer-v3-show-prices-toggle"
            className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-[#18181B] text-[#00E575] focus:ring-[#00E575]"
          />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-zinc-100">Einzelpreise je Komponente im PDF anzeigen</div>
            <div className="text-[11px] text-zinc-400 leading-tight mt-0.5">
              Zeigt in der Positionstabelle für jede Komponente Einzelpreis und Zwischensumme — sonst nur der Bündelpreis unten.
              Empfehlung: nur bei Kunden aktivieren, die explizit Preistransparenz einfordern.
            </div>
          </div>
        </label>
        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <button data-testid="offer-v3-create-btn"
            onClick={() => createOffer({ forceNew: false })}
            disabled={creating || !v3Config?.packageId || !v3Config?.lines?.length}
            className="h-14 w-full sm:w-auto sm:flex-1 bg-[#00E575] text-[#09090B] font-bold text-[15px] rounded-xl hover:bg-[#00C964] active:scale-[0.97] transition inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {creating ? <Loader2 className="animate-spin" size={18} /> : editingOfferId
              ? (editingLocked
                ? (<><FileText size={18} /> Als neue Version speichern (neue Nummer)</>)
                : (<><FileText size={18} /> Richtpreisangebot aktualisieren (Nummer bleibt)</>))
              : (<><FileText size={18} /> Als Richtpreisangebot erstellen</>)}
          </button>
          <button data-testid="offer-v3-orientation-btn"
            onClick={() => createOrientationOffer()}
            disabled={creating}
            title="Unverbindliches Orientierungsangebot mit Standard-Annahmen. Kein LLV, keine AGB, keine Signatur."
            className="h-14 shrink-0 sm:shrink sm:flex-1 px-4 bg-[#18181B] border border-[#00E575]/40 text-[#00E575] font-bold text-[14px] rounded-xl hover:bg-[#00E575]/10 active:scale-[0.97] transition inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {creating ? <Loader2 className="animate-spin" size={18} /> : (<><Sparkles size={18} /> Als Orientierungsangebot</>)}
          </button>
          {editingOfferId && (
            <button data-testid="offer-v3-create-new-btn"
              onClick={() => createOffer({ forceNew: true })}
              disabled={creating || !v3Config?.packageId || !v3Config?.lines?.length}
              title="Neues Angebot mit neuer Nummer — das alte bleibt als Historie."
              className="h-14 shrink-0 px-4 bg-[#18181B] border border-[#00E575]/40 text-[#00E575] font-bold text-[13.5px] rounded-xl hover:bg-[#00E575]/10 active:scale-[0.97] transition inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <FilePlus2 size={18} /> Als neues Angebot
            </button>
          )}
        </div>
      </div>
      </div>

      {/* Step ② KfW 458 Funding */}      {/* Step ② KfW 458 Funding */}
      <div ref={foerderungRef} className="scroll-mt-[168px]">
        <Kfw458Block
          projectId={project.id}
          previewGross={totalGross}
          onSaved={() => {
            // After saving the KfW step, smoothly reveal the Amortisations-Block
            // (so the user does not get bounced back to the top of the page).
            requestAnimationFrame(() => {
              scrollToStep("amortisation");
            });
          }}
        />
      </div>

      {/* Step ③ Amortisation / Wirtschaftlichkeit */}
      <SafeBoundary>
        <div ref={wirtschaftlichkeitRef} data-anchor="amortisation" className="scroll-mt-[168px]">
          <WirtschaftlichkeitBlock
            projectId={project.id}
            previewGross={totalGross}
            onChange={onUpdate}
          />
        </div>
      </SafeBoundary>

      {/* Novatus-Care Wartungsvertrag */}
      <a
        href={NOVATUS_CARE_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="novatus-care-offer-btn"
        className="block bg-gradient-to-br from-[#1A1A1F] to-[#0F0F12] border border-amber-500/30 rounded-2xl p-5 hover:border-amber-500/60 transition active:scale-[0.99]"
      >
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
            <Heart size={20} className="text-amber-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display text-base font-semibold leading-tight">Novatus Care – Wartungsvertrag</h3>
              <ExternalLink size={14} className="text-amber-400" />
            </div>
            <p className="text-[12px] text-zinc-400 mt-1 leading-snug font-medium">
              Kundenservice & Wartung direkt aus einer Hand. Jetzt mit anbieten und die Kundenbindung sichern.
            </p>
          </div>
        </div>
      </a>

      {/* Sticky Endpreis-Footer: der Legacy-Konfigurator wurde am 09.07.2026
          entfernt — der Footer war NUR im Legacy-Modus sichtbar und ist damit
          ebenfalls obsolet. Wird bewusst nicht mehr gerendert. */}

    </div>
  );
}
